import React, { useState, useEffect } from 'react';
import { Bell, Clock, Mail, Users, AlertTriangle, CheckCircle, Info, Settings } from 'lucide-react';
import api from '../utils/api';


const CompanyNotificationsSettings = ({ user }) => {
  const [settings, setSettings] = useState({
    // General notifications
    task_assignments: true,
    task_due_reminders: true,
    task_overdue_alerts: true,
    project_updates: true,
    
    // Team notifications
    team_member_added: true,
    team_member_removed: false,
    team_role_changes: true,
    
    // Client notifications
    client_portal_activity: true,
    client_feedback: true,
    client_approvals: true,
    
    // System notifications
    integration_status: true,
    system_maintenance: true,
    security_alerts: true,
    
    // Delivery settings
    email_notifications: false,
    slack_notifications: false,
    in_app_notifications: true,
    
    // Timing settings
    quiet_hours_enabled: false,
    quiet_hours_start: '22:00',
    quiet_hours_end: '08:00',
    
    // Digest settings
    daily_digest: false,
    weekly_digest: false,
    digest_time: '09:00'
  });

  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      // TODO: Implement API call to fetch company notification settings
      // const response = await api.get(`/company/notification-settings`);
      // setSettings(response.data);
    } catch (error) {
      console.error('Error fetching notification settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSettingChange = (key, value) => {
    setSettings(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const handleSave = async () => {
    try {
      setLoading(true);
      // TODO: Implement API call to save company notification settings
      // await api.put(`/company/notification-settings`, settings);
      alert('Company notification settings saved successfully!');
    } catch (error) {
      console.error('Error saving notification settings:', error);
      alert('Error saving settings. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const NotificationToggle = ({ id, label, description, icon: Icon, value, onChange }) => (
    <div className="flex items-start justify-between p-4 bg-gray-50 rounded-lg">
      <div className="flex items-start space-x-3">
        <div className="flex-shrink-0 mt-1">
          <Icon className="w-5 h-5 text-gray-600" />
        </div>
        <div>
          <label htmlFor={id} className="text-sm font-medium text-gray-900 cursor-pointer">
            {label}
          </label>
          <p className="text-sm text-gray-500 mt-1">{description}</p>
        </div>
      </div>
      <div className="flex-shrink-0">
        <button
          type="button"
          className={`${
            value ? 'bg-purple-600' : 'bg-gray-200'
          } relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2`}
          onClick={() => onChange(id, !value)}
        >
          <span
            className={`${
              value ? 'translate-x-5' : 'translate-x-0'
            } pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out`}
          />
        </button>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="p-6">
        <div className="animate-pulse">
          <div className="h-4 bg-gray-200 rounded w-1/4 mb-6"></div>
          <div className="space-y-4">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="h-16 bg-gray-200 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-4xl">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Company Notifications</h2>
        <p className="text-gray-600">Configure company-wide notification preferences for all team members.</p>
      </div>

      <div className="space-y-8">
        {/* Task & Project Notifications */}
        <div>
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
            <CheckCircle className="w-5 h-5 mr-2 text-green-600" />
            Task & Project Notifications
          </h3>
          <div className="space-y-3">
            <NotificationToggle
              id="task_assignments"
              label="Task Assignments"
              description="Notify team members when tasks are assigned to them"
              icon={CheckCircle}
              value={settings.task_assignments}
              onChange={handleSettingChange}
            />
            <NotificationToggle
              id="task_due_reminders"
              label="Task Due Reminders"
              description="Send reminders 24 hours before tasks are due"
              icon={Clock}
              value={settings.task_due_reminders}
              onChange={handleSettingChange}
            />
            <NotificationToggle
              id="task_overdue_alerts"
              label="Task Overdue Alerts"
              description="Alert team members when tasks become overdue"
              icon={AlertTriangle}
              value={settings.task_overdue_alerts}
              onChange={handleSettingChange}
            />
            <NotificationToggle
              id="project_updates"
              label="Project Updates"
              description="Notify about project status changes and milestones"
              icon={Info}
              value={settings.project_updates}
              onChange={handleSettingChange}
            />
          </div>
        </div>

        {/* Team Notifications */}
        <div>
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
            <Users className="w-5 h-5 mr-2 text-blue-600" />
            Team Notifications
          </h3>
          <div className="space-y-3">
            <NotificationToggle
              id="team_member_added"
              label="New Team Members"
              description="Notify when new members join the team"
              icon={Users}
              value={settings.team_member_added}
              onChange={handleSettingChange}
            />
            <NotificationToggle
              id="team_member_removed"
              label="Team Member Removed"
              description="Notify when members leave the team"
              icon={Users}
              value={settings.team_member_removed}
              onChange={handleSettingChange}
            />
            <NotificationToggle
              id="team_role_changes"
              label="Role Changes"
              description="Notify about team member role and permission changes"
              icon={Settings}
              value={settings.team_role_changes}
              onChange={handleSettingChange}
            />
          </div>
        </div>

        {/* Client Notifications */}
        <div>
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
            <Mail className="w-5 h-5 mr-2 text-purple-600" />
            Client Activity Notifications
          </h3>
          <div className="space-y-3">
            <NotificationToggle
              id="client_portal_activity"
              label="Client Portal Activity"
              description="Notify when clients log in or access the portal"
              icon={Mail}
              value={settings.client_portal_activity}
              onChange={handleSettingChange}
            />
            <NotificationToggle
              id="client_feedback"
              label="Client Feedback"
              description="Notify when clients leave comments or feedback"
              icon={Mail}
              value={settings.client_feedback}
              onChange={handleSettingChange}
            />
            <NotificationToggle
              id="client_approvals"
              label="Client Approvals"
              description="Notify when clients approve or request revisions"
              icon={CheckCircle}
              value={settings.client_approvals}
              onChange={handleSettingChange}
            />
          </div>
        </div>

        {/* System Notifications */}
        <div>
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
            <Settings className="w-5 h-5 mr-2 text-gray-600" />
            System Notifications
          </h3>
          <div className="space-y-3">
            <NotificationToggle
              id="integration_status"
              label="Integration Status"
              description="Notify about integration connections and failures"
              icon={Settings}
              value={settings.integration_status}
              onChange={handleSettingChange}
            />
            <NotificationToggle
              id="system_maintenance"
              label="System Maintenance"
              description="Notify about scheduled maintenance and updates"
              icon={Info}
              value={settings.system_maintenance}
              onChange={handleSettingChange}
            />
            <NotificationToggle
              id="security_alerts"
              label="Security Alerts"
              description="Notify about security events and login attempts"
              icon={AlertTriangle}
              value={settings.security_alerts}
              onChange={handleSettingChange}
            />
          </div>
        </div>
      </div>

      {/* Save Button */}
      <div className="mt-8 pt-6 border-t border-gray-200">
        <div className="flex justify-end">
          <button
            onClick={handleSave}
            disabled={loading}
            className="inline-flex items-center px-4 py-2 border border-transparent text-base font-medium rounded-md shadow-md text-white bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 disabled:opacity-50 transition-all"
          >
            {loading ? 'Saving...' : 'Save Settings'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CompanyNotificationsSettings;